import {
  SCRAPER_REQUEST_PARAM,
  targetScrapingParamInputFieldConfigMap,
  PROXY_POOL,
  TARGET,
} from '@decodo/utils';
import { useMemo } from 'react';

import { UseFormReturn } from '@/hooks/useForm';
import { useTranslation } from '@/lib/i18n/i18n.client';

import { isPrimaryParam } from '@/blocks/Scraper/helpers';

import Badge from '@/components/atoms/badge';
import LinkButton from '@/components/atoms/LinkButton';

import { useScraperTranslations } from '../scraperTranslations';
import { ScraperTemplateSelector } from '../ScraperTemplateSelector';
import { useScraper } from '../state/context';
import { FormCard } from './FormCard';
import FormCheckbox from './FormCheckbox';
import { FormField } from './FormField';
import ProxyPoolSelector from './ProxyPoolSelector';
import QueryField from './QueryField';

import iconExternalSrc from '~/svg/IconExternal.svg';
import { SvgIcon } from '@/components/SvgIcon';

type ConfigFormProps = {
  form: UseFormReturn<Record<string, string | boolean>>;
};

export const ConfigForm = ({ form }: ConfigFormProps) => {
  const { target, dashboardAction } = useScraper();
  const { texts } = useTranslation();
  const { getTranslation } = useScraperTranslations();

  const params = targetScrapingParamInputFieldConfigMap[target.targetKey];

  const jsRenderingParam = useMemo(
    () => params?.find((param) => param.key === SCRAPER_REQUEST_PARAM.HEADLESS),
    [params]
  );
  const proxyPoolParam = params?.find(
    (param) => param.key === SCRAPER_REQUEST_PARAM.PROXY_POOL
  );
  const locationParamKeys: string[] = [
    SCRAPER_REQUEST_PARAM.GEO,
    SCRAPER_REQUEST_PARAM.AMAZON_GEO,
    SCRAPER_REQUEST_PARAM.GEO_GOOGLE_AI_MODE,
  ];
  const languageParamKeys: string[] = [
    SCRAPER_REQUEST_PARAM.LOCALE,
    SCRAPER_REQUEST_PARAM.DOMAIN,
  ];
  const locationParam = params?.find((param) =>
    locationParamKeys.includes(param.key)
  );
  const languageParam = params?.find((param) =>
    languageParamKeys.includes(param.key)
  );
  const markdownParam = params?.find(
    (param) => param.key === SCRAPER_REQUEST_PARAM.MARKDOWN
  );
  const deviceTypeParam = params?.find(
    (param) => param.key === SCRAPER_REQUEST_PARAM.DEVICE_TYPE
  );

  const primaryParams = params?.filter(isPrimaryParam) || [];

  const isPremium =
    (form.values[SCRAPER_REQUEST_PARAM.PROXY_POOL] as string) ===
    PROXY_POOL.PREMIUM;
  const isWebTemplate = target.targetKey === TARGET.UNIVERSAL;
  const isMarkdownDisabled = isWebTemplate && !isPremium;

  const hasParametersSection = !!markdownParam || !!deviceTypeParam;
  const hasLocationRow = !!locationParam || !!languageParam;

  return (
    <div className='flex flex-1 h-full flex-col'>
      <FormCard className='mb-2' title={texts.scraper.target_template}>
        <ScraperTemplateSelector />
      </FormCard>

      {primaryParams?.map((param) => (
        <FormCard key={param.key} className='mb-2'>
          <div className='flex-col gap-5'>
            <QueryField form={form} param={param} />
          </div>
        </FormCard>
      ))}

      {jsRenderingParam && (
        <FormCard className='mb-2 overflow-visible'>
          <div key={jsRenderingParam.key} className='w-1/2 min-w-32'>
            <FormCheckbox param={jsRenderingParam} form={form} />
          </div>
        </FormCard>
      )}

      {proxyPoolParam ? (
        <FormCard
          title={getTranslation(proxyPoolParam.header.id ?? '') ?? 'Proxy pool'}
          className='mb-2'
        >
          <div className='flex flex-col gap-4'>
            <ProxyPoolSelector form={form} />
            {hasLocationRow && (
              <div className='flex flex-col gap-4 md:flex-row md:gap-6'>
                {locationParam && (
                  <div className='flex-1 min-w-32'>
                    <FormField form={form} param={locationParam} />
                  </div>
                )}
                {languageParam && (
                  <div className='flex-1 min-w-32'>
                    <FormField form={form} param={languageParam} />
                  </div>
                )}
              </div>
            )}
          </div>
        </FormCard>
      ) : (
        hasLocationRow && (
          <FormCard className='mb-2'>
            <div className='flex flex-col gap-4 md:flex-row md:gap-6'>
              {locationParam && (
                <div className='flex-1 min-w-32'>
                  <FormField form={form} param={locationParam} />
                </div>
              )}
              {languageParam && (
                <div className='flex-1 min-w-32'>
                  <FormField form={form} param={languageParam} />
                </div>
              )}
            </div>
          </FormCard>
        )
      )}

      {hasParametersSection && (
        <FormCard
          title={texts.scraper.parameters}
          headerRight={
            dashboardAction?.url ? (
              <LinkButton
                href={dashboardAction.url}
                openInNewTab={dashboardAction.openInNewTab}
                noStyles
                classNames={{
                  root: 'flex items-center gap-1 text-body-s-medium text-brand-500 hover:text-brand-600',
                  rightSection: 'h-4 w-4',
                }}
                rightSection={
                  <SvgIcon src={iconExternalSrc} className='h-4 w-4' />
                }
              >
                {texts.scraper.try_all_parameters_in_dashboard}
              </LinkButton>
            ) : null
          }
        >
          <div className='mt-2 flex flex-col gap-4'>
            {markdownParam && (
              <div className='flex items-center gap-2'>
                <FormCheckbox
                  param={markdownParam}
                  form={form}
                  isDisabled={isMarkdownDisabled}
                />
                <Badge
                  color='gray'
                  variant='soft'
                  classNames={{
                    root: 'text-label-s-medium font-mono px-2 py-[2px] uppercase bg-gray-200 text-gray-700 dark:bg-gray-dark-700 dark:text-gray-dark-200',
                  }}
                >
                  {texts.scraper.premium_proxy_pool_only}
                </Badge>
              </div>
            )}
            {deviceTypeParam && (
              <div className='w-full'>
                <FormField form={form} param={deviceTypeParam} />
              </div>
            )}
          </div>
        </FormCard>
      )}
    </div>
  );
};
